import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SERVICES = [
  { key: 'avgPest365', name: 'Pest365', color: '#2563eb' },
  { key: 'avgPest365Plus', name: 'Pest365+', color: '#8b5cf6' },
  { key: 'avgOneTime', name: 'One-Time', color: '#f59e0b' },
  { key: 'avgISV', name: 'Init. Visit', color: '#16a34a' },
  { key: 'avgMTC', name: 'Mosquito/Tick', color: '#dc2626' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '10px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: '12px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>{label}</div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '2px',
          }}
        >
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span style={{ fontWeight: 600 }}>
            {entry.value != null ? `$${entry.value.toFixed(2)}` : 'N/A'}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function WeeklyTrendChart({ data }) {
  const [hiddenLines, setHiddenLines] = useState(new Set());

  const chartData = useMemo(() => {
    if (!data || !data.dates) return [];
    return data.dates.map((date, i) => ({
      date,
      avgPest365: data.avgPest365 ? data.avgPest365[i] : null,
      avgPest365Plus: data.avgPest365Plus ? data.avgPest365Plus[i] : null,
      avgOneTime: data.avgOneTime ? data.avgOneTime[i] : null,
      avgISV: data.avgISV ? data.avgISV[i] : null,
      avgMTC: data.avgMTC ? data.avgMTC[i] : null,
    }));
  }, [data]);

  const handleLegendClick = (entry) => {
    setHiddenLines((prev) => {
      const next = new Set(prev);
      if (next.has(entry.dataKey)) {
        next.delete(entry.dataKey);
      } else {
        next.add(entry.dataKey);
      }
      return next;
    });
  };

  if (chartData.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Weekly Price Trends</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No trend data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Weekly Price Trends</div>
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            iconType="line"
            iconSize={14}
            wrapperStyle={{ fontSize: '12px', paddingTop: '8px', cursor: 'pointer' }}
            onClick={handleLegendClick}
            formatter={(value, entry) => (
              <span
                style={{
                  color: hiddenLines.has(entry.dataKey) ? '#d1d5db' : entry.color,
                  textDecoration: hiddenLines.has(entry.dataKey) ? 'line-through' : 'none',
                }}
              >
                {value}
              </span>
            )}
          />
          {SERVICES.map(({ key, name, color }) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={name}
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: color, stroke: '#fff', strokeWidth: 2 }}
              hide={hiddenLines.has(key)}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
